import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import type { Aircraft } from './aviation';
import type { Vec } from '../destruction/structure';
import type { PhysicsCity } from '../physics/world';
import type { Population } from './population';
import type { Soldier } from './military';
import { GeometryBuilder } from '../render/geometry-builder';
import { WATER_LEVEL } from '../world/terrain';

interface Descent {
  soldier: Soldier;
  goal: THREE.Vector3;
  velocity: THREE.Vector3;
  withdrawing: boolean;
  clearance: number;
  departure?: Aircraft;
}
export function parachuteGeometry(): THREE.BufferGeometry {
  const g = new GeometryBuilder(),
    segments = 12;
  for (let i = 0; i < segments; i++) {
    const a = (i * Math.PI * 2) / segments,
      b = ((i + 1) * Math.PI * 2) / segments,
      p = { x: Math.cos(a) * 2.2, y: 5.2, z: Math.sin(a) * 2.2 },
      q = { x: Math.cos(b) * 2.2, y: 5.2, z: Math.sin(b) * 2.2 },
      top = { x: 0, y: 6.1, z: 0 },
      normal = new THREE.Vector3()
        .crossVectors(new THREE.Vector3(-p.x, 0.9, -p.z), new THREE.Vector3(q.x - p.x, 0, q.z - p.z))
        .normalize(),
      color = i % 2 ? 0xaeb79b : 0xd1d4bd;
    // Fabric is visible from both above and below. The previous triangle order
    // faced down only, so a genuine god-view descent showed ropes without cloth.
    g.triangle(p, top, q, normal, color);
    g.triangle(p, q, top, normal.clone().negate(), color);
    const hand = { x: Math.cos(a) * 0.3, y: 1.45, z: Math.sin(a) * 0.3 },
      corners = [
        { x: hand.x - 0.015, y: hand.y, z: hand.z },
        { x: hand.x + 0.015, y: hand.y, z: hand.z },
        { x: p.x + 0.015, y: p.y, z: p.z },
        { x: p.x - 0.015, y: p.y, z: p.z },
      ];
    g.quad(corners[0], corners[1], corners[2], corners[3], { x: 0, y: 0, z: 1 }, 0x727967);
    g.quad(corners[3], corners[2], corners[1], corners[0], { x: 0, y: 0, z: -1 }, 0x727967);
  }
  return g.geometry();
}
/** A canopy is only a rendering of an actual original descending person.
 * Cancellation retains the descent; epoch reset is the only global clear. */
export class Paratroopers {
  descents = new Map<string, Descent>();
  landed = 0;
  lost = 0;
  readonly losses: { id: string; reason: string; position: number[]; goal: number[]; velocity: number[] }[] =
    [];
  onLand: (soldier: Soldier, withdrawing: boolean) => void = () => {};
  private epoch = 0;
  private mesh: THREE.InstancedMesh;
  private transform = new THREE.Object3D();
  constructor(
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.epoch = physics.life.epoch;
    this.mesh = new THREE.InstancedMesh(parachuteGeometry(), physics.material, 256);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = this.mesh.receiveShadow = true;
    physics.scene.add(this.mesh);
  }
  launch(soldier: Soldier, landing: Vec, velocity: Vec, clearanceSeconds = 0, departure?: Aircraft): boolean {
    const p = soldier.person;
    if (
      this.descents.size >= 256 ||
      this.descents.has(p.id) ||
      p.fatal ||
      p.state === 'incapacitated' ||
      p.p.y < landing.y + 8
    )
      return false;
    this.population.civil?.cancelTask(p, 'Original person leaving the aircraft under canopy');
    p.carrier = undefined;
    p.exposedSeat = false;
    p.controlled = true;
    p.hidden = false;
    p.state = 'parachuting';
    p.seatHeight = p.seatBlend = undefined;
    soldier.state = 'airborne';
    this.descents.set(p.id, {
      soldier,
      goal: new THREE.Vector3(landing.x, landing.y, landing.z),
      velocity: new THREE.Vector3(velocity.x, velocity.y, velocity.z),
      withdrawing: false,
      clearance: Math.max(0, Math.min(1, clearanceSeconds)),
      departure,
    });
    return true;
  }
  cancel(): void {
    for (const descent of this.descents.values()) descent.withdrawing = true;
  }
  clear(): void {
    this.descents.clear();
    this.mesh.count = 0;
    this.landed = this.lost = 0;
    this.losses.length = 0;
    this.epoch = this.physics.life.epoch;
  }
  private recordLoss(d: Descent, reason: string): void {
    this.losses.push({
      id: d.soldier.id,
      reason,
      position: d.soldier.person.p.toArray(),
      goal: d.goal.toArray(),
      velocity: d.velocity.toArray(),
    });
    if (this.losses.length > 128) this.losses.shift();
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) {
      this.clear();
      return;
    }
    for (const [id, d] of this.descents) {
      const p = d.soldier.person;
      if (p.fatal || p.state === 'incapacitated') {
        this.descents.delete(id);
        d.soldier.state = 'incapacitated';
        continue;
      }
      p.previous.copy(p.p);
      const steering = d.goal.clone().sub(p.p).setY(0).clampLength(0, 4),
        blend = 1 - Math.exp(-dt * 2.8);
      if (d.clearance > 0) d.clearance = Math.max(0, d.clearance - dt);
      else {
        // Keep relative carrier queries until physically separated. An elapsed
        // hatch timer is not proof of separation at higher aircraft speeds:
        // Rapier's old tail can still overlap the next world-space sweep.
        if (d.departure && p.p.distanceToSquared(d.departure.p) > 65 * 65) d.departure = undefined;
        d.velocity.x += (steering.x - d.velocity.x) * blend;
        d.velocity.z += (steering.z - d.velocity.z) * blend;
      }
      d.velocity.y = Math.max(-5.8, d.velocity.y - 9.8 * dt);
      const move = d.velocity.clone().multiplyScalar(dt),
        next = p.p.clone().add(move),
        // The query starts above the previous feet, not just above the next
        // endpoint. A 60 Hz descent can cross more than5cm in one step; starting
        // the ray below that floor falsely turned a safe landing into a crash.
        support = this.physics.ground(next, Math.max(0.05, -move.y + 0.05), 0.2);
      if (support && next.y <= support.y + 0.025) {
        next.y = support.y;
        if (
          !this.physics.occupiedBoxClear(
            p.p.clone().add(new THREE.Vector3(0, 0.85, 0)),
            { x: 0.24, y: 0.8, z: 0.24 },
            new THREE.Quaternion(),
            next.clone().sub(p.p),
          ) ||
          !this.population.footClear(p, next)
        ) {
          this.recordLoss(d, 'Landing body or nearby person obstructed');
          p.velocity.copy(d.velocity);
          this.population.incapacitate(p, p.p, 1.8);
          d.soldier.state = 'incapacitated';
          this.lost++;
          this.descents.delete(id);
          continue;
        }
        p.p.copy(next);
        p.velocity.set(0, 0, 0);
        p.controlled = true;
        p.state = 'wait';
        p.layer = 'street';
        p.support = support.owner;
        const network = this.population.civil?.network,
          node = network?.near(p.p, 2).find((n) => Math.abs(n.y - p.p.y) < 0.35 && network.lineOpen(p.p, n));
        if (node) {
          p.layer = node.layer;
          d.soldier.state = 'deployed';
          this.landed++;
          this.onLand(d.soldier, d.withdrawing);
        } else {
          this.recordLoss(d, 'Supported landing has no clear local navigation connection');
          this.population.incapacitate(p, p.p, 1.8);
          d.soldier.state = 'incapacitated';
          this.lost++;
        }
        this.descents.delete(id);
        continue;
      }
      if (next.y < WATER_LEVEL - 0.1) {
        this.recordLoss(d, 'Descent reached water without support');
        p.p.copy(next);
        p.fatal = true;
        p.carrier = undefined;
        this.population.incapacitate(p, p.p, 1.8);
        d.soldier.state = 'lost';
        this.lost++;
        this.descents.delete(id);
        continue;
      }
      const from = { x: p.p.x, y: p.p.y + 0.85, z: p.p.z },
        half = { x: 0.24, y: 0.8, z: 0.24 },
        rotation = new THREE.Quaternion(),
        carrier = d.departure?.cluster.body.isValid() ? d.departure : undefined;
      let clear = this.physics.occupiedBoxClear(from, half, rotation, move, carrier?.cluster.body);
      if (clear && carrier) {
        // The world sweep checks every other obstacle. Cast the real carrier
        // colliders with their own displacement too: a static-world cast made
        // a departing soldier hit the previous position of the moving hatch.
        const body = carrier.cluster.body,
          shape = new RAPIER.Cuboid(half.x, half.y, half.z),
          currentOrigin = carrier.cluster.origin
            .clone()
            .sub(carrier.anchor)
            .applyQuaternion(carrier.q)
            .add(carrier.p),
          physicalOrigin = body.translation(),
          physicalQ = new THREE.Quaternion().copy(body.rotation()),
          alignment = physicalQ.clone().multiply(carrier.q.clone().invert()),
          relativeFrom = new THREE.Vector3(from.x, from.y, from.z)
            .sub(currentOrigin)
            .applyQuaternion(alignment)
            .add(physicalOrigin),
          relativeRotation = alignment.clone().multiply(rotation),
          relativeMove = move.clone().applyQuaternion(alignment),
          carrierMove = new THREE.Vector3()
            .copy(carrier.state === 'wreck' ? body.linvel() : carrier.velocity)
            .multiplyScalar(dt)
            .applyQuaternion(alignment);
        for (let i = 0; i < body.numColliders(); i++) {
          // Controller poses are one kinematic step ahead of Rapier here.
          // Align the start frames before a relative-motion cast; otherwise a
          // faster aircraft's old hatch falsely overlaps an already exiting body.
          if (
            body
              .collider(i)
              .castShape(carrierMove, shape, relativeFrom, relativeRotation, relativeMove, 0.03, 1, true)
          ) {
            clear = false;
            break;
          }
        }
      }
      if (clear) p.p.copy(next);
      else {
        // A live collision, such as a falling section in the descent column,
        // enters the same casualty simulation instead of an endless airborne slot.
        this.recordLoss(d, 'Body collided during descent');
        p.velocity.copy(d.velocity);
        this.population.incapacitate(p, p.p, 1.8);
        d.soldier.state = 'incapacitated';
        this.descents.delete(id);
        this.lost++;
      }
    }
  }
  render(alpha: number): void {
    let index = 0;
    for (const d of this.descents.values()) {
      this.transform.position.copy(d.soldier.person.previous).lerp(d.soldier.person.p, alpha);
      this.transform.rotation.set(0, d.soldier.person.yaw, 0);
      this.transform.updateMatrix();
      this.mesh.setMatrixAt(index, this.transform.matrix);
      this.mesh.setColorAt(index, new THREE.Color(d.soldier.faction === 'friendly' ? 0xb9d39f : 0xd5d8d2));
      index++;
    }
    this.mesh.count = index;
    if (index) {
      this.mesh.instanceMatrix.needsUpdate = true;
      this.mesh.instanceColor!.needsUpdate = true;
    }
  }
  dispose(): void {
    this.clear();
    this.physics.scene.remove(this.mesh);
    this.mesh.geometry.dispose();
  }
}
